import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { login, registerUser } from "@/src/actions/authAction";
import { useAuth } from "@/src/provider/AuthProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { EyeIcon, EyeOffIcon, LogInIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FaSpinner } from "react-icons/fa6";
import { FiUserPlus } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import Login from "../Login";
import { toast } from "@/components/ui/use-toast";
import { Paths } from "@/src/constants/Paths";

const formSchema = z
  .object({
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email("This is not a valid email."),
    phoneNumber: z
      .string()
      .optional()
      .refine(
        (value) => !value || /^[0-9]{10}$/.test(value),
        { message: "Phone number must be a valid 10-digit number" }
      ),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(8, "Password must be at least 8 characters"),
    firstName: z.string().min(1, { message: "First Name is required" }),
    lastName: z.string().min(1, { message: "Last Name is required" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });
const Register = ({ className, fromRegister, handleClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { state } = location;
  const returnUrl = new URLSearchParams(location.search).get("returnUrl");
  const { setToken } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
  });
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  async function onSubmit(pld) {
    delete pld.confirmPassword;
    setIsLoading(true);
    dispatch(
      registerUser(pld, (response) => {
        setIsLoading(false);
        if (response && response.IsSuccess) {
          let payload = {
            username: form.getValues().email,
            password: form.getValues().password,
          };
          toast({
            variant: "success",
            title: "User registered!",
            description: response.Message,
          });
          dispatch(
            login(payload, (success, token) => {
              if (success) {
                setToken(token);
                if (fromRegister) {
                  const currentUrl = window.location.pathname + window.location.search;
                  const newUrl = currentUrl.includes("?")
                    ? `${currentUrl}&registration=success`
                    : `${currentUrl}?registration=success`;
                  navigate(newUrl, { replace: true });
                  handleClose();
                } else {
                  if (state && state.from && state.from == "listBook") {
                    navigate("/flights?registration=success");
                    return;
                  }
                  else if (returnUrl && returnUrl.length > 0) {
                    const newReturnUrl = returnUrl.includes("?")
                      ? `${returnUrl}&registration=success`
                      : `${returnUrl}?registration=success`;
                    navigate(newReturnUrl, { replace: true });
                  }
                  else navigate(Paths.HOME + "?registration=success")
                }
              }
            })
          );
        } else {
          toast({
            variant: "destructive",
            title: "Registration Failed",
            description: response?.Message || "Something went wrong during registration.",
          });
        }
      })
    );
  }
  return (
    <>
      {showLogin ? (
        <Login fromLogin={fromRegister} />
      ) : (
        <div className="p-2 lg:p-8">
          <div
            className={
              fromRegister
                ? ""
                : "bg-background mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[480px]  border shadow p-5 rounded"
            }
          >
            <div className="flex flex-col space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Create an account
              </h1>
              <p className="text-sm text-muted-foreground">
                Fill the form below to create an account.
              </p>
            </div>
            <div className={cn("grid gap-6", className)}>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-2">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <div className="grid gap-1">
                          <FormItem>
                            <Label className="sr-only" htmlFor="firstName">
                              First Name
                            </Label>
                            <Input
                              id="firstName"
                              type="firstName"
                              placeholder="First Name"
                              onChange={field.onChange}
                              disabled={isLoading}
                            />
                            <FormMessage />
                          </FormItem>
                        </div>
                      )}
                    ></FormField>

                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <div className="grid gap-1">
                          <FormItem>
                            <Label className="sr-only" htmlFor="lastName">
                              Last Name
                            </Label>
                            <Input
                              id="lastName"
                              type="lastName"
                              placeholder="Last Name"
                              onChange={field.onChange}
                              disabled={isLoading}
                            />
                            <FormMessage />
                          </FormItem>
                        </div>
                      )}
                    ></FormField>
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <div className="grid gap-1">
                          <FormItem>
                            <Label className="sr-only" htmlFor="email">
                              Email
                            </Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder="Email"
                              onChange={field.onChange}
                              disabled={isLoading}
                            />
                            <FormMessage />
                          </FormItem>
                        </div>
                      )}
                    ></FormField>
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <div className="grid gap-1">
                          <FormItem>
                            <Label className="sr-only" htmlFor="phoneNumber">
                              Phone Number
                            </Label>
                            <Input
                              id="phone"
                              type="tel"
                              placeholder="Phone Number"
                              onChange={field.onChange}
                              disabled={isLoading}
                            />
                            <FormMessage />
                          </FormItem>
                        </div>
                      )}
                    ></FormField>
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <div className="grid gap-1">
                          <FormItem>
                            <Label className="sr-only" htmlFor="password">
                              Password
                            </Label>
                            <Input
                              id="password"
                              placeholder="Password"
                              type="password"
                              onChange={field.onChange}
                              disabled={isLoading}
                            />
                            {/* <EyeOffIcon />
                        <EyeIcon /> */}
                            <FormMessage />
                          </FormItem>
                        </div>
                      )}
                    ></FormField>
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <div className="grid gap-1">
                          <FormItem>
                            <Label
                              className="sr-only"
                              htmlFor="confirmPassword"
                            >
                              Confirm Password
                            </Label>
                            <Input
                              id="confirmPassword"
                              placeholder="Confirm Password"
                              type="password"
                              onChange={field.onChange}
                              disabled={isLoading}
                            />
                            <FormMessage />
                          </FormItem>
                        </div>
                      )}
                    ></FormField>
                    <Button disabled={isLoading}>
                      {isLoading && (
                        <FaSpinner className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <FiUserPlus className="me-2 h-4 w-4 " />
                      <span>Sign Up</span>
                    </Button>
                  </div>
                </form>
              </Form>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-background px-2 text-muted-foreground">
                    Already registered?
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                type="button"
                disabled={isLoading}
                onClick={() => {
                  fromRegister ? setShowLogin(true) : navigate("/auth/login");
                }}
              >
                <LogInIcon className="me-2 h-4 w-4" /> Login
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Register;
